import delay from '../../utils/delay'

import { resultSuccess, resultError } from '../../interfaces/base'
import { appErrorFromAnything } from '../../utils/error'
import {
  DoCreativeAction,
  DoCreativesAction,
  UpdateCreative,
  GetCreative,
  SetCreativeComment,
  UpdateCardPosition,
  CreateCreative,
  ImportCreatives,
  FinalizeCreative,
  ValidateCreative,
  ICreativeService,
  GetCreativeBlueprint,
  GetCreativePreview,
  CanEdit,
  GetCreativePlacements,
  GetCreativeVersion,
  GetFormatsetPlacementForCreative,
  GetPreviewlink,
  GetCreativeForPreview,
} from '../../interfaces/services/creatives'
import { CreativeId, Creative } from '../../interfaces/entities/creative'

import { projects as mockedProjects } from '../../mocks/projects'
import { creatives as mockedCreatives } from '../../mocks/creatives'
import { MultifileCreative as mockedCreativeBlueprint } from '../../mocks/blueprints/creatives'

import defaultThumbnail from '../../assets/images/owl.jpg'
import { translateWithUserLocale } from '../../utils/i18n'

export const creativesServiceMock: (DI: any) => ICreativeService = (DI) => {
  const getCreativeForPreview: GetCreativeForPreview = async (id) => {
    await delay(1000)

    return resultSuccess({ creative: mockedCreatives[0], public: false })
  }

  const getCreative: GetCreative = async (id) => {
    await delay(1000)

    return resultSuccess({ ...mockedCreatives[0], project: mockedProjects[0] })
  }

  const getCreativePreviewlink: GetPreviewlink = async (id) => {
    await delay(1000)

    return resultSuccess({
      creative_uuid: 'creative-uuid',
      url: 'www.test.com',
      expire: '2022-12-30T16:00:30+01:00',
    })
  }

  const getCreativeBlueprint: GetCreativeBlueprint = async (
    creativeId,
    type,
  ) => {
    await delay(1000)

    return resultSuccess(mockedCreativeBlueprint)
  }

  const updateCreative: UpdateCreative = async (params) => {
    await delay(1000)

    return resultSuccess({ ...mockedCreatives[0] })
  }

  const importCreatives: ImportCreatives = async (formatsetId, ids) => {
    await delay(1000)

    return resultSuccess({ ...mockedCreatives[0] })
  }

  const finalizeCreative: FinalizeCreative = async (params) => {
    await delay(1000)

    return resultSuccess({ ...mockedCreatives[0] })
  }

  const validateCreative: ValidateCreative = async (params) => {
    await delay(1000)

    return resultSuccess({ ...mockedCreatives[0] })
  }

  const createCreative: CreateCreative = async (params) => {
    return resultSuccess({ ...mockedCreatives[0] })
  }

  const doCreativeAction: DoCreativeAction = async (action, creative) => {
    await delay(1000)

    const creativeCopy = {
      ...creative,
      status:
        creative.status.name === 'work_in_progress'
          ? { name: 'sent_to_agency', label: 'sent_to_agency' }
          : { name: 'work_in_progress', label: 'work_in_progress' },
    }

    return resultSuccess({ creative: creativeCopy, message: '', action })
  }

  const doCreativesAction: DoCreativesAction = async (action, creatives) => {
    await delay(1000)

    const creativeCopy = creatives.map((creative) => {
      return {
        ...creative,
        status:
          action.name === 'sent'
            ? { name: 'sent_to_agency', label: 'sent_to_agency' }
            : { name: 'work_in_progress', label: 'work_in_progress' },
      }
    })

    return resultSuccess({ creatives: creativeCopy, warnings: [] })
  }

  const updateCardPosition: UpdateCardPosition = async (params) => {
    await delay(1000)

    return resultSuccess({ ...mockedCreatives[0] })
  }

  const setCreativeComment: SetCreativeComment = async (
    creativeId: CreativeId,
    comment: string,
  ) => {
    await delay(1000)

    const creativeIndex = mockedCreatives.findIndex(
      (creative) => creative.id === creativeId,
    )
    if (creativeIndex !== -1) {
      return resultSuccess({ content: comment })
    } else {
      return resultError({
        title: translateWithUserLocale('error.creative_not_found_title'),
        message: translateWithUserLocale('error.creative_not_found_message'),
        statusCode: '404',
      })
    }
  }

  const getCreativeThumbnail = (creative: Creative) => {
    return defaultThumbnail
  }

  const getPreview: GetCreativePreview = async (_creative, _placement) => {
    try {
      return resultSuccess('')
    } catch (e) {
      return resultError(appErrorFromAnything(e))
    }
  }

  const getFormatsetPlacementForCreative: GetFormatsetPlacementForCreative = (
    creative: Creative,
    placement: string,
  ) => {
    return null
  }

  const canEdit: CanEdit = (_creative) => true

  const getPlacements: GetCreativePlacements = async (_creative) => {
    return [
      'RIGHT_COLUMN_STANDARD',
      'DESKTOP_FEED_STANDARD',
      'MOBILE_FEED_STANDARD',
      'MOBILE_FEED_BASIC',
      'MOBILE_INTERSTITIAL',
      'MOBILE_BANNER',
      'MOBILE_MEDIUM_RECTANGLE',
      'MOBILE_FULLWIDTH',
      'MOBILE_NATIVE',
      'FACEBOOK_REELS_MOBILE',
      'FACEBOOK_REELS_POSTLOOP',
      'FACEBOOK_REELS_STICKER',
      'FACEBOOK_REELS_BANNER',
      'INSTAGRAM_STANDARD',
      'INSTAGRAM_STORY',
      'INSTAGRAM_REELS',
      'INSTAGRAM_PROFILE_REELS',
      'AUDIENCE_NETWORK_INSTREAM_VIDEO',
      'AUDIENCE_NETWORK_OUTSTREAM_VIDEO',
      'AUDIENCE_NETWORK_INSTREAM_VIDEO_MOBILE',
      'AUDIENCE_NETWORK_REWARDED_VIDEO',
      'INSTREAM_VIDEO_DESKTOP',
      'INSTREAM_VIDEO_MOBILE',
      'MESSENGER_MOBILE_INBOX_MEDIA',
      'SUGGESTED_VIDEO_DESKTOP',
      'SUGGESTED_VIDEO_MOBILE',
      'MARKETPLACE_MOBILE',
    ]
  }

  const getCreativeVersion: GetCreativeVersion = (creative) => {
    return `${creative.type}_1`
  }

  return {
    canEdit,
    getCreative,
    getPreview,
    getCreativeForPreview,
    getFormatsetPlacementForCreative,
    getCreativeBlueprint,
    doCreativeAction,
    doCreativesAction,
    createCreative,
    updateCreative,
    importCreatives,
    finalizeCreative,
    validateCreative,
    setCreativeComment,
    updateCardPosition,
    getCreativeThumbnail,
    getPlacements,
    getCreativeVersion,
    getCreativePreviewlink,
  }
}

export default creativesServiceMock
